import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Crown, UserCircle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { useSupportStore } from '../../../store/useSupportStore';
import { useAdminTenantStore } from '../../../store/useAdminTenantStore';
import {
  SupportTicketStatus,
  SupportTicketType,
  type SupportTicketDto,
} from '../../../api/types/admin';
import { formatDateTime } from '../../../utils/dateUtils';

const TYPE_KEY: Record<number, string> = {
  [SupportTicketType.Question]: 'question',
  [SupportTicketType.Problem]: 'problem',
  [SupportTicketType.NewTenantRequest]: 'newTenantRequest',
  [SupportTicketType.MeetingRequest]: 'meetingRequest',
  [SupportTicketType.Other]: 'other',
};

export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);

  const { saving, getById, reply, setStatus } = useSupportStore();
  const { tenants, fetchAll: fetchTenants } = useAdminTenantStore();

  const [ticket, setTicket] = useState<SupportTicketDto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const t0 = await getById(id);
      if (!cancelled) {
        setTicket(t0);
        setLoaded(true);
      }
      if (tenants.length === 0) fetchTenants();
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getById, fetchTenants, tenants.length]);

  if (!loaded) {
    return <p className="text-[13px] text-slate-500 text-center py-12">{t('common:actions.loading')}</p>;
  }

  if (!ticket || !ticket.id) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('admin:support.errors.notFound')}
          action={
            <Link to="/admin/support">
              <Button variant="secondary" leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}>
                {t('admin:support.detail.back')}
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const tenantName = ticket.tenantId
    ? tenants.find((tn) => tn.id === ticket.tenantId)?.name ?? ticket.tenantId
    : t('admin:support.fields.anonymous');

  const handleStatusChange = async (newStatus: SupportTicketStatus) => {
    if (!ticket.id) return;
    const ok = await setStatus(ticket.id, newStatus);
    if (ok) {
      const fresh = await getById(ticket.id);
      setTicket(fresh);
    }
  };

  const handleReply = async () => {
    if (!ticket.id || !replyText.trim()) return;
    const ok = await reply(ticket.id, replyText.trim());
    if (ok) {
      setReplyText('');
      const fresh = await getById(ticket.id);
      setTicket(fresh);
    }
  };

  const sortedReplies = [...(ticket.replies ?? [])].sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return da - db;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        onClick={() => navigate('/admin/support')}
        leftIcon={<ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />}
      >
        {t('admin:support.detail.back')}
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {ticket.subject}
              </h1>
              <Badge>{t(`admin:support.types.${TYPE_KEY[ticket.type ?? -1] ?? 'other'}`)}</Badge>
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('admin:support.fields.from')}: {ticket.name || ticket.email} · {tenantName}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={ticket.status ?? SupportTicketStatus.New}
              onChange={(e) => handleStatusChange(Number(e.target.value) as SupportTicketStatus)}
              disabled={saving}
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value={SupportTicketStatus.New}>{t('admin:support.status.new')}</option>
              <option value={SupportTicketStatus.InProgress}>{t('admin:support.status.inProgress')}</option>
              <option value={SupportTicketStatus.Closed}>{t('admin:support.status.closed')}</option>
            </select>
          </div>
        </div>
      </div>

      <Card>
        <div className="text-[12px] text-slate-500 mb-2">
          {t('admin:support.fields.createdAt')}: {formatDateTime(ticket.createdAt)}
          {ticket.closedAt && (
            <>
              {' · '}
              {t('admin:support.fields.closedAt')}: {formatDateTime(ticket.closedAt)}
            </>
          )}
        </div>
        <div className="kicker mb-2">{t('admin:support.detail.message')}</div>
        <p className="text-[13px] text-slate-800 whitespace-pre-wrap">{ticket.message}</p>
      </Card>

      {sortedReplies.length > 0 && (
        <Card title={t('admin:support.detail.history')} noPadding>
          <ul className="divide-y divide-slate-100">
            {sortedReplies.map((r) => (
              <li key={r.id} className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  {r.fromAdmin ? (
                    <>
                      <Crown className="w-3 h-3 text-[#00A871]" strokeWidth={2} />
                      <span className="text-[12px] font-medium text-slate-900">
                        {t('admin:support.detail.you')}
                      </span>
                    </>
                  ) : (
                    <>
                      <UserCircle className="w-3 h-3 text-slate-500" strokeWidth={2} />
                      <span className="text-[12px] font-medium text-slate-900">
                        {t('admin:support.detail.customer')}
                      </span>
                    </>
                  )}
                  <span className="text-[11px] text-slate-500">{formatDateTime(r.createdAt)}</span>
                </div>
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{r.body}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <div className="space-y-2">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={5}
            placeholder={t('admin:support.detail.replyPlaceholder')}
            className="w-full px-3 py-2 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 resize-y"
          />
          <div className="flex justify-end">
            <Button
              onClick={handleReply}
              disabled={saving || !replyText.trim()}
              leftIcon={<Send className="w-3.5 h-3.5" strokeWidth={2} />}
            >
              {t('admin:support.detail.send')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
